import { useNavigate, useParams } from 'react-router-dom'
import { usePage } from '../api/hooks'
import { usePagePoolArticles } from '../api/hooks/useArticles'
import { useSlotArticles, PageSlotRenderer } from '../components/page-renderer/PageSlotRenderer'
import PageLoader from '../components/common/PageLoader'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { BlockContent, PageBlock } from '../api/types'

function pageBlocksToSlots(blocks: PageBlock[]): CanvasSlot[] {
  return blocks
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((b) => ({
      id: b.id,
      type: b.type,
      order: b.order,
      config: b.config,
      columnBlocks: b.columnContent
        ? Object.fromEntries(
            Object.entries(b.columnContent).map(([k, colBlocks]) => [
              k,
              (colBlocks ?? []).map((cb) => ({ id: cb.id, type: cb.type })),
            ]),
          )
        : undefined,
    }))
}

function pageBlocksToContentMap(blocks: PageBlock[]): Record<string, BlockContent> {
  const map: Record<string, BlockContent> = {}
  for (const b of blocks) {
    if (b.content) map[b.id] = b.content
    if (b.columnContent) {
      for (const [colKey, colBlocks] of Object.entries(b.columnContent)) {
        for (const cb of colBlocks ?? []) {
          if (cb.content) map[`${b.id}:${colKey}:${cb.id}`] = cb.content
        }
      }
    }
  }
  return map
}

export default function PageViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: page, isPending: isPagePending, isError } = usePage(id ?? '')
  const { articles: poolArticles, isPending: isPoolPending } = usePagePoolArticles(page?.pool)

  const slots = page ? pageBlocksToSlots(page.blocks) : []
  const contentMap = page ? pageBlocksToContentMap(page.blocks) : {}
  const slotArticleMap = useSlotArticles(slots, contentMap, page?.pool ? poolArticles : undefined)

  if (isPagePending || isPoolPending) {
    return <PageLoader />
  }

  if (isError || !page) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-fg-secondary">
        Page not found.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
      <div className="space-y-10">
        {slots.map((slot) => (
          <PageSlotRenderer
            key={slot.id}
            slot={slot}
            articles={slotArticleMap[slot.id] ?? []}
            slotArticleMap={slotArticleMap}
            onArticleClick={(article) => navigate(`/wire/articles/${article.id}`)}
          />
        ))}
      </div>
    </div>
  )
}
